import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, TriangleAlert } from "lucide-react";

import { Button, Card, Checkbox, FormField, MultiSelectField, Skeleton, StatusBadge } from "../ui";
import { listarBancosAtivos, listarConveniosAtivos } from "../../services/joinService";
import { listarPreFiltros, removerPreFiltro, salvarPreFiltro } from "../../services/preFiltroService";
import { SwalWithTheme } from "../../utils/sweetalert";

const FILTRO_VAZIO = { id: null, nome: "", ativo: true, restante: false, ordem: 0, bancos: [], convenios: [], tipos: [] };

function valoresPorDimensao(preFiltro, dimensao) {
  return (preFiltro.valores || []).filter((valor) => valor.dimensao === dimensao).map((valor) => valor.valor);
}

function rotuloDosValores(preFiltro, dimensao) {
  return (preFiltro.valores || [])
    .filter((valor) => valor.dimensao === dimensao)
    .map((valor) => valor.rotulo || valor.valor);
}

/**
 * Grupos de fechamento (FlamexPay, Imobiliário, Consórcio, Seguro, Consignado).
 *
 * Os valores vão para a Join como ela os entende: "ou" dentro da dimensão, "e" entre dimensões.
 * Por isso a tela fala em banco/convênio/tipo separadamente, sem esconder a regra de quem cadastra.
 */
export default function PreFiltrosSection() {
  const [preFiltros, setPreFiltros] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [edicao, setEdicao] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [grupos, bancosJoin, conveniosJoin] = await Promise.all([
        listarPreFiltros().catch(() => []),
        listarBancosAtivos().catch(() => []),
        listarConveniosAtivos().catch(() => []),
      ]);
      setPreFiltros(grupos);
      setBancos(bancosJoin);
      setConvenios(conveniosJoin);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const opcoesBanco = useMemo(
    () => bancos.map((banco) => ({ value: String(banco.bancoId ?? banco.id), label: banco.bancoNome ?? banco.nome })),
    [bancos]
  );
  const opcoesConvenio = useMemo(
    () => convenios.map((convenio) => ({
      value: String(convenio.convenioId ?? convenio.id),
      label: convenio.convenioNome ?? convenio.nome,
    })),
    [convenios]
  );
  const opcoesTipo = useMemo(() => {
    const tipos = new Set();
    convenios.forEach((convenio) => (convenio.tiposConvenio || []).forEach((tipo) => {
      const nome = typeof tipo === "string" ? tipo : tipo?.nome;
      if (nome) tipos.add(nome);
    }));
    return Array.from(tipos).sort().map((tipo) => ({ value: tipo, label: tipo }));
  }, [convenios]);

  // Banco que a Join passou a listar e ainda não está em nenhum grupo cai no "restante" sem avisar.
  // Avisar é o que evita o fechamento mudar em silêncio quando entra banco novo.
  const bancosForaDeGrupo = useMemo(() => {
    const usados = new Set();
    preFiltros.filter((grupo) => grupo.ativo).forEach((grupo) => {
      valoresPorDimensao(grupo, "BANCO").forEach((valor) => usados.add(String(valor)));
    });
    return opcoesBanco.filter((banco) => !usados.has(banco.value));
  }, [preFiltros, opcoesBanco]);

  const temRestante = preFiltros.some((grupo) => grupo.restante);

  const abrirNovo = () => setEdicao({ ...FILTRO_VAZIO, ordem: preFiltros.length });

  const abrirEdicao = (grupo) => setEdicao({
    id: grupo.id,
    nome: grupo.nome,
    ativo: grupo.ativo,
    restante: grupo.restante,
    ordem: grupo.ordem,
    bancos: valoresPorDimensao(grupo, "BANCO"),
    convenios: valoresPorDimensao(grupo, "CONVENIO"),
    tipos: valoresPorDimensao(grupo, "TIPO_PRODUTO"),
  });

  const salvar = async () => {
    if (!edicao?.nome?.trim()) {
      SwalWithTheme.error("Falta o nome", "Dê um nome ao pré-filtro.");
      return;
    }
    const rotulo = (opcoes, valor) => opcoes.find((opcao) => opcao.value === valor)?.label || valor;
    const valores = edicao.restante
      ? []
      : [
        ...edicao.bancos.map((valor) => ({ dimensao: "BANCO", valor, rotulo: rotulo(opcoesBanco, valor) })),
        ...edicao.convenios.map((valor) => ({ dimensao: "CONVENIO", valor, rotulo: rotulo(opcoesConvenio, valor) })),
        ...edicao.tipos.map((valor) => ({ dimensao: "TIPO_PRODUTO", valor, rotulo: valor })),
      ];

    setSalvando(true);
    try {
      await salvarPreFiltro({
        id: edicao.id,
        nome: edicao.nome.trim(),
        ativo: edicao.ativo,
        restante: edicao.restante,
        ordem: Number(edicao.ordem) || 0,
        valores,
      });
      setEdicao(null);
      await carregar();
      SwalWithTheme.success("Pronto", "Pré-filtro salvo.");
    } catch (erro) {
      SwalWithTheme.error("Não deu", erro?.response?.data?.message || "Não foi possível salvar o pré-filtro.");
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (grupo) => {
    const confirmacao = await SwalWithTheme.fire({
      title: `Remover "${grupo.nome}"?`,
      text: "Quem estiver usando esse pré-filtro volta a filtrar na mão.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacao.isConfirmed) return;
    try {
      await removerPreFiltro(grupo.id);
      await carregar();
    } catch (erro) {
      SwalWithTheme.error("Não deu", erro?.response?.data?.message || "Não foi possível remover.");
    }
  };

  return (
    <Card
      title="Pré-filtros"
      actions={[
        <Button key="novo-pre-filtro" size="sm" onClick={abrirNovo} disabled={carregando}>
          <Plus className="h-4 w-4" />
          Novo pré-filtro
        </Button>,
      ]}
    >
      <p className="mb-4 text-sm text-muted-foreground">
        Grupos prontos de banco, convênio e tipo de produto — FlamexPay, Imobiliário, Consórcio, Seguro — para usar
        como filtro em Repasse, Vendas e Vida do agente. Dentro de uma dimensão vale qualquer um dos valores; entre
        dimensões, todos precisam bater. O grupo marcado como <strong className="text-foreground">restante</strong> é
        calculado: tudo que não cai nos outros.
      </p>

      {carregando ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      ) : (
        <div className="space-y-2">
          {preFiltros.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              Nenhum pré-filtro ainda. Crie um para o time parar de montar o filtro de cabeça.
            </p>
          ) : null}

          {preFiltros.map((grupo) => (
            <div key={grupo.id} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-border p-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-foreground">{grupo.nome}</span>
                  {grupo.restante ? <StatusBadge status="Restante" variant="info" /> : null}
                  {!grupo.ativo ? <StatusBadge status="Inativo" variant="danger" /> : null}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {grupo.restante
                    ? "Tudo que não está nos outros grupos, calculado contra os bancos ativos da Join."
                    : ["BANCO", "CONVENIO", "TIPO_PRODUTO"]
                      .map((dimensao) => {
                        const rotulos = rotuloDosValores(grupo, dimensao);
                        if (!rotulos.length) return null;
                        const nome = dimensao === "BANCO" ? "Bancos" : dimensao === "CONVENIO" ? "Convênios" : "Tipos";
                        return `${nome}: ${rotulos.join(", ")}`;
                      })
                      .filter(Boolean)
                      .join(" · ")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => abrirEdicao(grupo)}>Editar</Button>
                <Button variant="ghost" size="sm" onClick={() => remover(grupo)} aria-label={`Remover ${grupo.nome}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!carregando && bancosForaDeGrupo.length > 0 && temRestante ? (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm text-warning">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            <strong>{bancosForaDeGrupo.length} banco(s) sem grupo</strong>, hoje contados no restante:{" "}
            {bancosForaDeGrupo.slice(0, 6).map((banco) => banco.label).join(", ")}
            {bancosForaDeGrupo.length > 6 ? ` e mais ${bancosForaDeGrupo.length - 6}` : ""}. Se algum deles for de
            outro grupo, classifique antes do próximo fechamento.
          </span>
        </div>
      ) : null}

      {edicao ? (
        <div className="mt-4 space-y-4 rounded-2xl border border-border bg-muted/20 p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FormField
              label="Nome"
              value={edicao.nome}
              placeholder="FLAMEX PAY"
              onChange={(event) => setEdicao((atual) => ({ ...atual, nome: event.target.value }))}
            />
            <FormField
              label="Ordem na lista"
              type="number"
              value={edicao.ordem}
              onChange={(event) => setEdicao((atual) => ({ ...atual, ordem: event.target.value }))}
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-foreground">
              <Checkbox
                checked={edicao.ativo}
                onCheckedChange={(valor) => setEdicao((atual) => ({ ...atual, ativo: valor }))}
              />
              Ativo
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-foreground">
              <Checkbox
                checked={edicao.restante}
                onCheckedChange={(valor) => setEdicao((atual) => ({ ...atual, restante: valor }))}
              />
              É o grupo &quot;restante&quot;
              {temRestante && !edicao.restante ? (
                <span className="text-xs text-muted-foreground">(já existe um)</span>
              ) : null}
            </label>
          </div>

          {edicao.restante ? (
            <p className="rounded-xl border border-info/40 bg-info/10 p-3 text-sm text-info">
              Este grupo não tem lista própria: ele é tudo que sobra dos outros, recalculado a cada consulta contra os
              bancos ativos da Join. Banco novo entra aqui sozinho.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              <MultiSelectField
                label="Bancos"
                placeholder="Todos"
                options={opcoesBanco}
                selectedValues={edicao.bancos}
                onChange={(valores) => setEdicao((atual) => ({ ...atual, bancos: valores }))}
              />
              <MultiSelectField
                label="Convênios"
                placeholder="Todos"
                options={opcoesConvenio}
                selectedValues={edicao.convenios}
                onChange={(valores) => setEdicao((atual) => ({ ...atual, convenios: valores }))}
              />
              <MultiSelectField
                label="Tipos de produto"
                placeholder="Todos"
                options={opcoesTipo}
                selectedValues={edicao.tipos}
                onChange={(valores) => setEdicao((atual) => ({ ...atual, tipos: valores }))}
              />
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEdicao(null)}>Cancelar</Button>
            <Button size="sm" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando..." : "Salvar pré-filtro"}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
